'use client'

import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { FileIcon, FileUpIcon, SaveIcon, XIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormField } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { FileSelector } from '@/components/partials/files'
import { CreateProductPictureImportParams, ProductPictureImportType } from '@/types/product-picture-import'
import { LoadingIndicator } from '@/components/partials'
import { FormModeEnum, MimeTypes } from '@/enums'
import { FileType } from '@/types/file'
import { useMetaStore } from '@/store'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  initialData?: ProductPictureImportType | null
  onSubmit: (data: CreateProductPictureImportParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type ProductPictureImportFormRef = {
  handleValidationErrors: (error: any) => void
}

type FormData = {
  fileId: string
  removeExistingPictures: boolean
  optimizeImages: boolean
}

const ProductPictureImportForm = forwardRef<ProductPictureImportFormRef, PropsType>(
  ({ initialData, onSubmit, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { metaState } = useMetaStore()

    // State for a file
    const [file, setFile] = useState<FileType | null>(initialData?.file ?? null)

    const schema = useMemo(() => {
      return z.object({
        fileId: z.string().min(1, t('required')),
        removeExistingPictures: z.boolean().optional(),
        optimizeImages: z.boolean().optional()
      })
    }, [t])

    const getDefaultValues = useCallback((): FormData => {
      return {
        fileId: initialData?.file?.id ?? '',
        removeExistingPictures: false,
        optimizeImages: true
      }
    }, [initialData])

    const form = useForm<FormData>({
      resolver: zodResolver(schema) as Resolver<FormData>,
      defaultValues: getDefaultValues(),
      mode: 'onChange'
    })

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
      }
    }))

    const formErrors = form.formState.errors

    const handleFileChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setFile(null)
        form.setValue('fileId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setFile(files[0])
        form.setValue('fileId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setFile(files)
        form.setValue('fileId', files?.id || '', { shouldValidate: true })
      }
    }

    const handleSubmit = (data: FormData) => {
      onSubmit({
        fileId: data.fileId,
        removeExistingPictures: data.removeExistingPictures,
        optimizeImages: data.optimizeImages
      })
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUpIcon className="h-5 w-5" />
                {t('bulk_picture_import')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid items-start justify-items-start gap-4 xl:grid-cols-2">
                {/* File */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name={'fileId'}
                    render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                  />
                  <div className="flex items-center gap-2">
                    <FileIcon className="h-5 w-5" />
                    <h3 className="font-semibold">{t('file')}</h3>
                  </div>
                  <div>
                    <FileSelector
                      multiple={false}
                      onlyImages={false}
                      optimizeImageSelection={false}
                      types={[MimeTypes.ZIP]}
                      value={file}
                      onChange={handleFileChange}
                      buttonLabel={t('select_file')}
                      buttonLabelChange={t('change_file')}
                      noSelectionText={t('no_file_selected')}
                      allowMainSelection={true}
                      initialMainId={initialData?.file?.id}
                    />
                    {formErrors.fileId && (
                      <p className="text-destructive mt-2 text-sm">
                        {String(formErrors.fileId.message || t('required'))}
                      </p>
                    )}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">{t('options')}</h3>

                  {/* Optimize Images */}
                  <FormField
                    control={form.control}
                    name="optimizeImages"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="optimizeImages"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading || form.formState.isSubmitting}
                        />
                        <Label
                          htmlFor="optimizeImages"
                          className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('optimize_images')}
                        </Label>
                      </div>
                    )}
                  />

                  {/* Remove Existing Pictures */}
                  <FormField
                    control={form.control}
                    name="removeExistingPictures"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="removeExistingPictures"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading || form.formState.isSubmitting}
                        />
                        <Label
                          htmlFor="removeExistingPictures"
                          className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('remove_existing_pictures')}
                        </Label>
                      </div>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                  <SaveIcon className="h-4 w-4" />
                  {isLoading || form.formState.isSubmitting ? t('saving') : t(mode)}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    )
  }
)

ProductPictureImportForm.displayName = 'ProductPictureImportForm'

export default ProductPictureImportForm
